import useSWRFetcher, {
  SWRFetchError,
} from "../../../../../utils/hooks/commonly-stored/fetcher";
import { useBoardFromContext } from "../../../../../utils/hooks/commonly-stored/uploaded-board/board";
import { Board, ElementPlacement } from "../../../../../reducers/uploadedBoard";
import useSWR from "swr";
import settings from "../../../../../constants/settings";
import LoadingIcon from "../../../../common/LoadingIcon";
import { Error, HistoryContainer, NoHistory } from "./BOMHistoryStyles";
import {
  FlashPCBTable,
  TableCell,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
} from "../../../../../constants/Styles";
import React, { useEffect, useState } from "react";
import { datetimeStringToHumanReadableDatetime } from "../../../../../utils/valueFormatting";
import Placement from "../../ElementsTable/common/Placement";
import UserPill from "../../../../common/UserPill";
import FlashPCBSelect, { OptionType } from "../../../../common/FlashPCBSelect";
import Icon from "../../../../common/Icon";
import usePart from "../../../../../utils/hooks/commonly-stored/parts";
import { UserSummary } from "../../../../../reducers/models/users";
import { BLANK } from "../../../../../utils/hooks/commonly-stored/uploaded-board/billOfMaterials";

function sortRecordsByPerformedAt(a: BOMHistoryRecord, b: BOMHistoryRecord) {
  if (a.performed_at > b.performed_at) {
    return -1;
  } else if (a.performed_at == b.performed_at) {
    return 0;
  }
  return 1;
}

interface BOMHistoryRecord {
  performed_by: UserSummary | null;
  performed_at: string;
  action: string;
  element_id: string;
  element_name: string;
  value_text: string | null;
  do_not_populate: boolean | null;
  is_consignment: boolean | null;
  part_id: string | null;
  placement: ElementPlacement | null;
}

interface BOMHistoryResponse {
  records: BOMHistoryRecord[];
}

function useBOMHistory() {
  const fetcher = useSWRFetcher<BOMHistoryResponse>();
  const board = useBoardFromContext().board as Board;

  const { data, error, isValidating } = useSWR<
    BOMHistoryResponse,
    SWRFetchError
  >(`${settings.api_url}/boards/${board.id}/bom-history`, fetcher, {
    revalidateOnFocus: false,
  });

  return {
    bomHistory: data,
    loading: isValidating,
    errored: error !== undefined,
  };
}

function MaybeBoolean({ on }: { on: boolean | null }) {
  if (on === null) {
    return null;
  }
  if (on) {
    return <Icon src={"/icons/check_gray.svg"} alt={"Yes"} />;
  } else {
    return <Icon src={"/icons/remove_gray.svg"} alt={"Yes"} />;
  }
}

export function BOMHistoryRow({ record }: { record: BOMHistoryRecord }) {
  const { part } = usePart(record.part_id);

  return (
    <TableRow>
      <TableCell>
        {datetimeStringToHumanReadableDatetime(record.performed_at)}
      </TableCell>
      <TableCell>
        {record.performed_by !== null && (
          <UserPill user={record.performed_by} />
        )}
      </TableCell>
      <TableCell>{record.action}</TableCell>
      <TableCell>{record.element_name}</TableCell>
      <TableCell>{record.value_text}</TableCell>
      <TableCell>
        <MaybeBoolean
          on={record.do_not_populate === null ? null : !record.do_not_populate}
        />
      </TableCell>
      <TableCell>
        <MaybeBoolean on={record.is_consignment} />
      </TableCell>
      <TableCell>{part?.name || BLANK}</TableCell>
      <TableCell>
        {record.placement !== null && (
          <Placement placement={record.placement} />
        )}
      </TableCell>
    </TableRow>
  );
}

export default function BOMHistory() {
  const { bomHistory, loading, errored } = useBOMHistory();
  const [elementNameOptions, setElementNameOptions] = useState<OptionType[]>(
    [],
  );
  const [elementNameToFilterFor, setElementNameToFilterFor] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (bomHistory !== undefined && bomHistory.records.length > 0) {
      const names = new Set(
        bomHistory.records.map((record) => record.element_name),
      );
      setElementNameOptions(
        Array.from(names).map((name) => ({ label: name, value: name })),
      );
    }
  }, [bomHistory]);

  if (errored) {
    return <Error>Failed to get history</Error>;
  }

  if (bomHistory === undefined || loading) {
    return <LoadingIcon text={"Loading history..."} centerHorizontally />;
  }

  if (bomHistory.records.length === 0) {
    return <NoHistory>No history to show</NoHistory>;
  }

  const selectElementName = (newValue: OptionType | null) => {
    if (newValue === null) {
      setElementNameToFilterFor(null);
    } else {
      setElementNameToFilterFor(newValue.value);
    }
  };

  return (
    <HistoryContainer>
      <FlashPCBSelect
        label={"Filter by element name"}
        isClearable
        value={
          elementNameToFilterFor === null
            ? null
            : { label: elementNameToFilterFor, value: elementNameToFilterFor }
        }
        options={elementNameOptions}
        onChange={selectElementName}
      />
      <FlashPCBTable>
        <thead>
          <TableHeaderRow>
            <TableHeaderCell width={"12rem"}>Performed at</TableHeaderCell>
            <TableHeaderCell width={"20rem"}>Performed by</TableHeaderCell>
            <TableHeaderCell width={"8rem"}>Action</TableHeaderCell>
            <TableHeaderCell width={"8rem"}>Element</TableHeaderCell>
            <TableHeaderCell>Value text</TableHeaderCell>
            <TableHeaderCell>Populate</TableHeaderCell>
            <TableHeaderCell>Consignment</TableHeaderCell>
            <TableHeaderCell>Part</TableHeaderCell>
            <TableHeaderCell>Placement</TableHeaderCell>
          </TableHeaderRow>
        </thead>
        <tbody>
          {bomHistory.records
            .filter((record) =>
              elementNameToFilterFor === null
                ? true
                : record.element_name === elementNameToFilterFor,
            )
            .sort(sortRecordsByPerformedAt)
            .map((record) => {
              return (
                <BOMHistoryRow key={JSON.stringify(record)} record={record} />
              );
            })}
        </tbody>
      </FlashPCBTable>
    </HistoryContainer>
  );
}
